/**
 * assistant-modules/sentiment-lite.ts
 *
 * Lightweight, dependency-free client-side sentiment analyser.
 * Produces a signed score in [-1.0, 1.0] and a labelled SentimentResult.
 *
 * Design decisions:
 *   • Lexicon-based (no LLM call) — must complete in < 1 ms, zero latency
 *   • Covers general English, legal/professional vocabulary, and Swahili
 *     common expressions used in the Kenyan context
 *   • Negation window: up to 3 words after "not / no / never / don't" flip sign
 *   • Intensifier stacking: "very very good" → 1.0 × 1.45² capped at 2.0
 *   • Score is capped at ±1.0 after all modifiers
 *
 * For richer sentiment (e.g. LLM-based), use classifySentiment() from
 * src/lib/sentiment.ts on the server side, or pass the sentiment from the
 * meta SSE event (evt.sentiment) instead of calling this function.
 *
 * Exported:
 *   computeClientSentiment(text: string): SentimentResult
 */

import type { SentimentResult } from './personality-types';

// ─── Lexicons ─────────────────────────────────────────────────────────────────

/**
 * Positive word → base score (0.1 – 1.0).
 * Higher = stronger signal.
 */
const POSITIVE: Readonly<Record<string, number>> = {
  // ── Strong positives ────────────────────────────────────────────────────
  excellent:     0.9,  outstanding:   0.9,  superb:        0.9,
  exceptional:   0.9,  brilliant:     0.85, perfect:       0.85,
  wonderful:     0.85, fantastic:     0.85, amazing:       0.85,
  extraordinary: 0.85, phenomenal:   0.85, magnificent:   0.85,
  flawless:      0.8,  impeccable:   0.8,  exemplary:     0.8,
  // ── Clear positives ─────────────────────────────────────────────────────
  great:         0.75, good:          0.65, better:        0.6,
  improved:      0.6,  resolved:      0.7,  solved:        0.7,
  fixed:         0.65, completed:    0.65,  approved:      0.7,
  confirmed:     0.65, success:       0.75, successful:    0.75,
  achieved:      0.7,  accomplished: 0.7,  correct:       0.6,
  accurate:      0.6,  clear:         0.5,  helpful:       0.65,
  useful:        0.6,  effective:    0.65,  // duplicate 'efficient' removed
  safe:          0.6,  secure:        0.6,  protected:     0.6,
  granted:       0.65, upheld:        0.65, winning:       0.7,
  won:           0.7,  victory:       0.75, settled:       0.65,
  // ── Emotional positives ─────────────────────────────────────────────────
  happy:         0.75, joy:           0.8,  delighted:     0.8,
  pleased:       0.7,  glad:          0.65, love:          0.8,
  excited:       0.75, hopeful:       0.65, optimistic:    0.7,
  encouraged:    0.65, motivated:    0.65,  inspired:      0.7,
  proud:         0.7,  satisfied:     0.7,  relieved:      0.65,
  grateful:      0.75, thankful:      0.75, appreciate:    0.65,
  thanks:        0.6,  thank:         0.55, cheers:        0.5,
  // ── Professional positives ───────────────────────────────────────────────
  // (efficient already defined above)
  professional:  0.55, thorough:      0.6,
  diligent:      0.6,  dedicated:    0.6,  committed:     0.6,
  timely:        0.55, prompt:        0.55, responsive:    0.55,
  agreed:        0.55, accepted:      0.6,  compliant:     0.5,
  valid:         0.5,  lawful:        0.5,  legitimate:    0.5,
  // ── Swahili / Kenyan context ─────────────────────────────────────────────
  asante:        0.7,  sawa:          0.5,  vizuri:        0.65,
  nzuri:         0.65, bora:          0.65, hongera:       0.75,
  // ── Moderate positives ───────────────────────────────────────────────────
  fine:          0.4,  ok:            0.3,  okay:          0.3,
  alright:       0.35, done:          0.4,  ready:         0.4,
};

/**
 * Negative word → base score (0.1 – 1.0).
 * Higher magnitude = stronger signal.
 */
const NEGATIVE: Readonly<Record<string, number>> = {
  // ── Strong negatives ────────────────────────────────────────────────────
  terrible:      0.9,  awful:         0.9,  horrible:      0.9,
  dreadful:      0.9,  catastrophic:  0.9,  disastrous:    0.9,
  devastating:   0.85, appalling:    0.85,  atrocious:     0.85,
  outrageous:    0.8,  inexcusable:  0.85,  unacceptable:  0.8,
  // ── Clear negatives ──────────────────────────────────────────────────────
  bad:           0.7,  wrong:         0.7,  broken:        0.65,
  failed:        0.75, failure:       0.75, rejected:      0.75,
  denied:        0.7,  dismissed:    0.65,  lost:          0.65,
  losing:        0.65, error:         0.6,  mistake:       0.6,
  delay:         0.55, delayed:       0.55, overdue:       0.65,
  late:          0.5,  missed:        0.6,  missing:       0.55,
  problem:       0.55, issue:         0.45, concern:       0.4,
  difficult:     0.4,  challenging:  0.35,  complicated:   0.35,
  unclear:       0.4,  confusing:    0.45,  misleading:    0.6,
  incorrect:     0.6,  inaccurate:   0.6,   invalid:       0.6,
  unlawful:      0.7,  illegal:       0.75, fraudulent:    0.8,
  // ── Emotional negatives ─────────────────────────────────────────────────
  angry:         0.7,  frustrated:   0.7,   furious:       0.85,
  annoyed:       0.6,  irritated:    0.6,   upset:         0.65,
  worried:       0.55, anxious:      0.55,  stressed:      0.6,
  afraid:        0.65, scared:       0.65,  panicked:      0.75,
  sad:           0.6,  unhappy:      0.65,  disappointed:  0.7,
  hopeless:      0.8,  desperate:    0.75,  helpless:      0.7,
  hate:          0.8,  dislike:      0.6,
  // ── Professional negatives ───────────────────────────────────────────────
  negligent:     0.75, incompetent:  0.8,   unprofessional: 0.75,
  dishonest:     0.8,  corrupt:      0.85,  unfair:        0.7,
  breach:       0.75, violation:    0.75,  penalty:       0.65,
  liability:     0.5,  risk:         0.4,   dangerous:     0.65,
  // ── Swahili / Kenyan context ─────────────────────────────────────────────
  vibaya:        0.7,  mbaya:        0.7,   shida:         0.5,
  tatizo:        0.55,
};

/**
 * Words that multiply the intensity of the next sentiment word.
 * Applied to the immediately following scored token.
 */
const INTENSIFIERS: Readonly<Record<string, number>> = {
  very:         1.3,  extremely:    1.5,  incredibly:   1.5,
  absolutely:   1.4,  completely:   1.4,  utterly:      1.4,
  truly:        1.25, really:       1.2,  quite:        1.1,
  particularly: 1.15, especially:   1.2,  deeply:       1.25,
  highly:       1.25, terribly:     1.35, awfully:      1.35,
  totally:      1.3,  remarkably:   1.25, exceptionally: 1.3,
  profoundly:   1.3,  enormously:  1.35,  tremendously:  1.35,
  sana:         1.3,  kabisa:       1.4,  // Swahili intensifiers
};

/**
 * Negation words — flip the sign of sentiment for the next NEGATION_WINDOW tokens.
 */
const NEGATION_WORDS = new Set([
  'not', 'no', 'never', "don't", 'dont', 'cannot', "can't", 'cant',
  'neither', 'nor', "isn't", 'isnt', "wasn't", 'wasnt', "aren't", 'arent',
  "haven't", 'havent', "hasn't", 'hasnt', "won't", 'wont', "wouldn't",
  'wouldnt', 'without', 'fail', 'lacking', 'lack', 'absent', 'hardly',
  'scarcely', 'barely', 'rarely',
]);

const NEGATION_WINDOW = 3; // tokens after negation word that are flipped

// ─── Tokeniser ────────────────────────────────────────────────────────────────

/** Split text into lowercase tokens, stripping punctuation but preserving contractions. */
function tokenise(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/["""'']/g, '') // remove fancy quotes
    .replace(/[^\w'\s-]/g, ' ') // replace punctuation with spaces
    .split(/\s+/)
    .filter(t => t.length > 0)
    .map(t => t.replace(/^'+|'+$/g, '')); // strip leading/trailing apostrophes
}

// ─── Core scoring ─────────────────────────────────────────────────────────────

/**
 * Compute a sentiment score for a text string.
 *
 * Algorithm:
 *  1. Tokenise into lowercase words
 *  2. Walk tokens left-to-right, tracking:
 *     • intensifier multiplier stack (accumulated for next sentiment word)
 *     • negation state (inverts sign for next N tokens)
 *  3. For each token: look up in POSITIVE / NEGATIVE lexicons
 *     → apply intensifiers → apply negation → accumulate
 *  4. Normalise raw score by number of sentiment tokens found
 *  5. Clamp to [-1.0, 1.0]
 *
 * @param text  Any natural language text (user message)
 * @returns     SentimentResult with score, label, and signal counts
 */
export function computeClientSentiment(text: string): SentimentResult {
  if (!text || text.trim().length === 0) {
    return { score: 0, label: 'neutral', signals: { positive: 0, negative: 0 } };
  }

  const tokens = tokenise(text);
  if (tokens.length === 0) {
    return { score: 0, label: 'neutral', signals: { positive: 0, negative: 0 } };
  }

  let rawScore    = 0;
  let tokenCount  = 0;
  let intensifier = 1.0;
  let negation    = 0; // countdown: > 0 means next tokens have flipped sign
  let positiveHits = 0;
  let negativeHits = 0;

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];

    // Check negation trigger
    if (NEGATION_WORDS.has(token)) {
      negation    = NEGATION_WINDOW;
      intensifier = 1.0;
      continue;
    }

    // Check intensifier
    const intensifierMult = INTENSIFIERS[token];
    if (intensifierMult !== undefined) {
      intensifier = Math.min(intensifier * intensifierMult, 2.0); // cap at 2×
      continue;
    }

    // Score positive word
    const posMag = POSITIVE[token];
    if (posMag !== undefined) {
      const sign  = negation > 0 ? -1 : 1;
      const delta = sign * posMag * intensifier;
      rawScore    += delta;
      tokenCount  += 1;
      if (sign > 0) positiveHits++; else negativeHits++;
      intensifier = 1.0;
      if (negation > 0) negation--;
      continue;
    }

    // Score negative word
    const negMag = NEGATIVE[token];
    if (negMag !== undefined) {
      const sign  = negation > 0 ? 1 : -1; // negation flips negative → positive
      const delta = sign * negMag * intensifier;
      rawScore    += delta;
      tokenCount  += 1;
      if (sign < 0) negativeHits++; else positiveHits++;
      intensifier = 1.0;
      if (negation > 0) negation--;
      continue;
    }

    // Non-sentiment token — decrement negation window if active
    if (negation > 0) negation--;
    intensifier = 1.0; // reset intensifier if no sentiment follows
  }

  // Normalise: divide by sentiment token count, or a minimum of 1
  const normalised = tokenCount > 0 ? rawScore / Math.max(1, tokenCount) : 0;

  // Clamp to [-1, 1] and round to 3 decimal places
  const score = parseFloat(Math.max(-1.0, Math.min(1.0, normalised)).toFixed(3));

  const label = scoreToLabel(score);

  return {
    score,
    label,
    signals: { positive: positiveHits, negative: negativeHits },
  };
}

// ─── Label mapping ────────────────────────────────────────────────────────────

function scoreToLabel(score: number): SentimentResult['label'] {
  if      (score >=  0.50) return 'very_positive';
  else if (score >=  0.15) return 'positive';
  else if (score <= -0.50) return 'very_negative';
  else if (score <= -0.15) return 'negative';
  else                     return 'neutral';
}

// ─── Convenience: map to [-1, 1] from backend ExtendedSentiment ──────────────

/**
 * Convert the server-side ExtendedSentiment string (from src/lib/sentiment.ts
 * or the SSE meta event's `sentiment` field) to a numeric score compatible
 * with evolvePersonality().
 *
 * Use this when the meta event provides a sentiment value so we don't need
 * to run the client-side lexicon at all.
 */
export function extendedSentimentToScore(
  sentiment: 'frustrated' | 'anxious' | 'neutral' | 'grateful' | 'urgent' | 'relieved' | string,
): number {
  const map: Record<string, number> = {
    very_positive:  0.80,
    positive:       0.40,
    grateful:       0.60,
    relieved:       0.45,
    neutral:        0.00,
    anxious:       -0.30,
    urgent:        -0.20, // urgent isn't strictly negative but it signals stress
    frustrated:    -0.60,
    very_negative: -0.80,
    negative:      -0.40,
  };
  return map[sentiment] ?? 0.0;
}

// ─── LLM-based approach (recommended for high-stakes evolution) ───────────────

/**
 * ALTERNATIVE APPROACH — LLM-based sentiment via the SSE pipeline.
 *
 * Rather than running the client-side lexicon, you can instruct the backend
 * to return sentiment in the `meta` SSE event. This is already partially
 * wired: `evt.sentiment` in handlers/chat.ts drives the brainwave animation.
 *
 * To use LLM sentiment for personality evolution instead:
 *
 *   1. In handlers/chat.ts `onEvent`, when `evt.type === 'meta'`:
 *      if (evt.sentiment !== undefined) {
 *        const score = extendedSentimentToScore(evt.sentiment);
 *        _pendingSentimentScore = score;  // store for evolution after 'done'
 *      }
 *
 *   2. In the 'done' event handler:
 *      const score = _pendingSentimentScore ?? computeClientSentiment(text).score;
 *      // evolve with whichever is available
 *
 * This gives you server-side accuracy (LLM-classified) while maintaining
 * the client-side lexicon as a reliable fallback for offline or fast-path.
 */
export const LLM_SENTIMENT_INTEGRATION_NOTE = `
  Server SSE 'meta' event emits evt.sentiment as one of:
  frustrated | anxious | neutral | grateful | urgent | relieved

  Use extendedSentimentToScore(evt.sentiment) to convert to [-1, 1]
  and pass to evolvePersonality() in place of computeClientSentiment().

  This is the recommended approach — the lexicon is the fallback.
` as const;